import React, { useState } from 'react';
import PropTypes from 'prop-types';
import List from '@material-ui/core/List';
import Icon from 'components/Utils/NewIcon';
import UIUtils from 'services/UIUtils';
import DefaultTextField from 'components/Utils/DefaultTextField';
import SelectableListItem from 'components/Utils/SelectableListItem';
import ProgressMessage from 'components/Utils/ProgressMessage';
import StaffUtils, { StaffBadge } from 'services/StaffUtils';
import PerformanceUtils from 'services/PerformanceUtils';
import './style.scss';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';

const StaffSearch = ({ onSelect, ignoreAdmins, ignorePhysicians, hideInactive }) => {
  const dispatch = useAppDispatch();

  const [results, setResults] = useState([]);
  const [typingTimeout, setTypingTimeout] = useState(undefined);
  const [searchTerm, setSearchTerm] = useState(undefined);
  const [clickedStaff, setClickedStaff] = useState(undefined);
  const [searching, setSearching] = useState(false);

  async function enterPressed(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      clearTimeout(typingTimeout);
      if (searchTerm !== undefined && searchTerm !== '') await searchStaff(searchTerm);
    }
  }

  function handleSearchChange(event) {
    clearTimeout(typingTimeout);
    let value = event.target.value;

    let timeoutHandle = setTimeout(async () => {
      if (value !== undefined && value !== '') await searchStaff(value);
    }, UIUtils.SEARCH_TYPE_TIMEOUT);

    setSearchTerm(value);
    setTypingTimeout(timeoutHandle);
  }

  async function searchStaff(value) {
    setSearching(true);

    let result;

    if (ignoreAdmins) {
      result = await dispatch(api.Physicians.getPhysicians({ name: value }));
    } else if (ignorePhysicians) {
      result = await dispatch(api.Admins.getAdmins({ search: value }));
    } else {
      result = (await dispatch(api.Staff.searchStaff({ name: value }))).map(
        (it) => it.physician || it.admin,
      );
    }

    result = result.map((staff) => {
      return {
        ...staff,
        inactive: staff.inactive || false,
      };
    });

    if (hideInactive === true) result = StaffUtils.filterActiveUsers(result);
    else result = PerformanceUtils.sortBy(result, ['inactive'], false);

    setResults(result);
    setSearching(false);
  }

  function selectStaff(staff) {
    setClickedStaff(staff);
    if (onSelect !== undefined) onSelect(staff);
  }

  return (
    <div className="search-column full-height-panel scroll-list staff-search">
      <div className="search-field">
        {!searching && <Icon src="magnifying-glass" />}

        <ProgressMessage size={20} show={searching} />
        <DefaultTextField
          name="searchTerm"
          autoFocus={true}
          label="Search by name"
          value={searchTerm}
          onKeyDown={enterPressed}
          onChange={handleSearchChange}
        />
      </div>
      <div className="list">
        <List>
          {results.map((staff) => (
            <SelectableStaffBadge
              key={staff.id}
              staff={staff}
              selected={!!clickedStaff && clickedStaff.id === staff.id}
              selectStaff={selectStaff}
            />
          ))}
        </List>
      </div>
    </div>
  );
};

const SelectableStaffBadge = ({ staff, selected, selectStaff }) => {
  function onClick() {
    selectStaff(staff);
  }

  return (
    <SelectableListItem
      selected={selected}
      onClick={onClick}
      item={<StaffBadge user={staff} />}
      special={staff.inactive}
    />
  );
};

StaffSearch.propTypes = {
  onSelect: PropTypes.func,
  ignoreAdmins: PropTypes.bool,
  hideInactive: PropTypes.bool,
};

export default StaffSearch;
